import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge component
const StatusBadge = ({ status }) => {
  const colors = {
    new: 'bg-blue-100 text-blue-800',
    contacted: 'bg-yellow-100 text-yellow-800',
    qualified: 'bg-purple-100 text-purple-800',
    proposal: 'bg-indigo-100 text-indigo-800',
    negotiation: 'bg-orange-100 text-orange-800',
    won: 'bg-green-100 text-green-800',
    lost: 'bg-red-100 text-red-800'
  };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

// Lead Card Component
const LeadCard = ({ lead, onSelect, isSelected }) => (
  <div 
    onClick={() => onSelect(lead)}
    className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
      isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-300'
    }`}
  >
    <div className="flex justify-between items-start mb-2">
      <h3 className="font-semibold text-gray-900 truncate">{lead.company_name}</h3>
      <StatusBadge status={lead.status} />
    </div>
    {lead.contact_name && (
      <p className="text-sm text-gray-600">{lead.contact_name}</p>
    )}
    {lead.industry && (
      <p className="text-xs text-gray-500 mt-1">{lead.industry}</p>
    )}
    <div className="flex justify-between items-center mt-3">
      <span className="text-xs text-gray-400">
        {lead.city}{lead.state ? `, ${lead.state}` : ''}
      </span>
      {lead.estimated_value && (
        <span className="text-sm font-medium text-green-600">
          ${lead.estimated_value.toLocaleString()}
        </span>
      )}
    </div>
  </div>
);

// Stats Card Component
const StatsCard = ({ title, value, subValue, icon, color }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-5`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className={`text-2xl font-bold mt-1 ${color || 'text-gray-900'}`}>{value}</p>
        {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
      </div>
      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${color ? color.replace('text-', 'bg-').replace('-600', '-100') : 'bg-blue-100'}`}>
        <span className="text-xl">{icon}</span>
      </div>
    </div>
  </div>
);

const CRMDashboard = () => {
  const navigate = useNavigate();
  
  // State
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('leads');
  
  // Leads state
  const [leads, setLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadDetails, setLeadDetails] = useState(null);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Stats state
  const [stats, setStats] = useState(null);
  
  // Modal states
  const [showAddLead, setShowAddLead] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showInteractionModal, setShowInteractionModal] = useState(false);
  
  // Form states
  const [newLead, setNewLead] = useState({
    company_name: '', industry: '', company_size: '', email: '', phone: '',
    website: '', city: '', state: '', contact_name: '', contact_title: '',
    contact_email: '', contact_phone: '', estimated_value: '', notes: ''
  });
  const [emailForm, setEmailForm] = useState({ subject: '', html_content: '' });
  const [interactionForm, setInteractionForm] = useState({ type: 'note', subject: '', content: '', outcome: '' });
  
  // Templates state
  const [templates, setTemplates] = useState([]);
  const [newTemplate, setNewTemplate] = useState({ name: '', subject: '', html_content: '', category: 'outreach' });
  
  // Accounting state
  const [accountingEntries, setAccountingEntries] = useState([]);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [newEntry, setNewEntry] = useState({
    type: 'invoice', category: 'subscription', amount: '', description: '', 
    due_date: '', business_id: '', status: 'pending'
  });

  // Funnel state
  const [funnelData, setFunnelData] = useState(null);
  
  // Timeline state
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  
  // AI Suggestions state
  const [suggestions, setSuggestions] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  
  // Sequences state
  const [sequences, setSequences] = useState([]);
  const [showAddSequence, setShowAddSequence] = useState(false);
  const [newSequence, setNewSequence] = useState({
    name: '', description: '', trigger: 'manual', provider: 'resend',
    steps: [{ step_number: 1, delay_days: 0, subject: '', html_content: '' }]
  });

  // Pipeline state
  const [pipelineStatus, setPipelineStatus] = useState(null);
  const [pipelineRunning, setPipelineRunning] = useState(false);
  const [pipelineResults, setPipelineResults] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [schedulerToggling, setSchedulerToggling] = useState(false);

  // Fetch user and check access
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
        if (response.data.role !== 'super_admin') {
          navigate('/dashboard');
          return;
        }
        setUser(response.data);
        setLoading(false);
      } catch (error) {
        navigate('/');
      }
    };
    checkAuth();
  }, [navigate]);

  // Fetch data when tab changes
  useEffect(() => {
    if (!loading && user) {
      if (activeTab === 'leads') fetchLeads();
      if (activeTab === 'stats') fetchStats();
      if (activeTab === 'templates') fetchTemplates();
      if (activeTab === 'accounting') fetchAccounting();
      if (activeTab === 'funnel') fetchFunnel();
      if (activeTab === 'sequences') fetchSequences();
      if (activeTab === 'pipeline') fetchPipelineStatus();
    }
  }, [activeTab, loading, user]);

  const fetchLeads = useCallback(async () => {
    setLeadsLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      
      const response = await axios.get(
        `${BACKEND_URL}/api/crm/leads?${params.toString()}`,
        { withCredentials: true }
      );
      setLeads(response.data.leads || []);
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLeadsLoading(false);
    }
  }, [searchQuery, statusFilter]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/crm/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/crm/templates`, { withCredentials: true });
      setTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchAccounting = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/crm/accounting`, { withCredentials: true });
      setAccountingEntries(response.data.entries || []);
    } catch (error) {
      console.error('Error fetching accounting:', error);
    }
  };

  const fetchFunnel = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${BACKEND_URL}/api/crm/funnel`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFunnelData(response.data);
    } catch (error) {
      console.error('Error fetching funnel:', error);
    }
  };

  const fetchSequences = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${BACKEND_URL}/api/crm/sequences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSequences(response.data.sequences || []);
    } catch (error) {
      console.error('Error fetching sequences:', error);
    }
  };

  const fetchTimeline = async (leadId) => {
    setTimelineLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${BACKEND_URL}/api/crm/leads/${leadId}/timeline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTimeline(response.data.timeline || []);
    } catch (error) {
      console.error('Error fetching timeline:', error);
    } finally {
      setTimelineLoading(false);
    }
  };

  const fetchSuggestions = async (leadId) => {
    setSuggestionsLoading(true);
    setSuggestions(null);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${BACKEND_URL}/api/crm/leads/${leadId}/suggestions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(response.data);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handleCreateSequence = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${BACKEND_URL}/api/crm/sequences`, newSequence, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSequences([response.data, ...sequences]);
      setShowAddSequence(false);
      setNewSequence({
        name: '', description: '', trigger: 'manual', provider: 'resend',
        steps: [{ step_number: 1, delay_days: 0, subject: '', html_content: '' }]
      });
    } catch (error) {
      console.error('Error creating sequence:', error);
    }
  };

  const handleExportInstantly = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${BACKEND_URL}/api/crm/export/leads/instantly`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `altrunic_leads_instantly_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting leads:', error);
    }
  };

  const fetchPipelineStatus = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const [statusRes, schedulerRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/pipeline/status`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/pipeline/scheduler/status`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setPipelineStatus(statusRes.data);
      setSchedulerStatus(schedulerRes.data);
    } catch (error) {
      console.error('Error fetching pipeline status:', error);
    }
  };

  const toggleScheduler = async (enabled, intervalHours) => {
    setSchedulerToggling(true);
    try {
      const token = localStorage.getItem('session_token');
      const body = { enabled };
      if (intervalHours !== undefined) body.interval_hours = intervalHours;
      const response = await axios.post(`${BACKEND_URL}/api/pipeline/scheduler/toggle`, body, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedulerStatus(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Error toggling scheduler:', error);
    } finally {
      setSchedulerToggling(false);
    }
  };

  const runPipeline = async () => {
    setPipelineRunning(true);
    setPipelineResults(null);
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${BACKEND_URL}/api/pipeline/process`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPipelineResults(response.data);
      fetchPipelineStatus();
    } catch (error) {
      console.error('Error running pipeline:', error);
    } finally {
      setPipelineRunning(false);
    }
  };

  const fetchLeadDetails = async (leadId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/crm/leads/${leadId}`, { withCredentials: true });
      setLeadDetails(response.data);
    } catch (error) {
      console.error('Error fetching lead details:', error);
    }
  };

  // Handle lead selection
  const handleLeadSelect = (lead) => {
    setSelectedLead(lead);
    fetchLeadDetails(lead.lead_id);
    fetchTimeline(lead.lead_id);
    fetchSuggestions(lead.lead_id);
  };

  // Create new lead
  const handleCreateLead = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${BACKEND_URL}/api/crm/leads`, newLead, { withCredentials: true });
      setLeads([response.data, ...leads]);
      setShowAddLead(false);
      setNewLead({
        company_name: '', industry: '', company_size: '', email: '', phone: '',
        website: '', city: '', state: '', contact_name: '', contact_title: '',
        contact_email: '', contact_phone: '', estimated_value: '', notes: ''
      });
    } catch (error) {
      console.error('Error creating lead:', error);
      alert('Failed to create lead');
    }
  };

  // Update lead status
  const handleStatusChange = async (newStatus) => {
    if (!selectedLead) return;
    try {
      await axios.put(
        `${BACKEND_URL}/api/crm/leads/${selectedLead.lead_id}`,
        { status: newStatus },
        { withCredentials: true }
      );
      // Refresh leads and details
      fetchLeads();
      fetchLeadDetails(selectedLead.lead_id);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Send email
  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!selectedLead) return;
    
    const recipientEmail = leadDetails?.lead?.contact_email || leadDetails?.lead?.email;
    if (!recipientEmail) {
      alert('No email address found for this lead');
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/crm/email/send`, {
        recipient_email: recipientEmail,
        subject: emailForm.subject,
        html_content: emailForm.html_content,
        lead_id: selectedLead.lead_id
      }, { withCredentials: true });
      
      setShowEmailModal(false);
      setEmailForm({ subject: '', html_content: '' });
      fetchLeadDetails(selectedLead.lead_id);
      alert('Email sent successfully!');
    } catch (error) {
      console.error('Error sending email:', error);
      alert('Failed to send email: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Add interaction
  const handleAddInteraction = async (e) => {
    e.preventDefault();
    if (!selectedLead) return;
    
    try {
      await axios.post(
        `${BACKEND_URL}/api/crm/leads/${selectedLead.lead_id}/interactions`,
        interactionForm,
        { withCredentials: true }
      );
      
      setShowInteractionModal(false);
      setInteractionForm({ type: 'note', subject: '', content: '', outcome: '' });
      fetchLeadDetails(selectedLead.lead_id);
    } catch (error) {
      console.error('Error adding interaction:', error);
    }
  };

  // Import leads
  const handleImport = async (e) => {
    e.preventDefault();
    const file = e.target.elements.file?.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/crm/leads/import`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      alert(`Imported ${response.data.imported_count} leads out of ${response.data.total_rows}`);
      setShowImport(false);
      fetchLeads();
    } catch (error) {
      console.error('Import error:', error);
      alert('Import failed: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Create template
  const handleCreateTemplate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/crm/templates`, newTemplate, { withCredentials: true });
      setShowTemplateModal(false);
      setNewTemplate({ name: '', subject: '', html_content: '', category: 'outreach' });
      fetchTemplates();
    } catch (error) {
      console.error('Error creating template:', error);
    }
  };

  // Create accounting entry
  const handleCreateEntry = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/crm/accounting`, newEntry, { withCredentials: true });
      setShowAddEntry(false);
      setNewEntry({
        type: 'invoice', category: 'subscription', amount: '', description: '', 
        due_date: '', business_id: '', status: 'pending'
      });
      fetchAccounting();
    } catch (error) {
      console.error('Error creating entry:', error);
    }
  };

  // Export leads
  const handleExport = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/crm/export/leads?format=csv`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `crm_leads_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-blue-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading CRM...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-gray-50 to-stone-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin')}
                className="text-gray-500 hover:text-gray-700"
              >
                ← Back to Super Admin
              </button>
              <h1 className="text-2xl font-bold text-blue-600">Altrunic</h1>
              <span className="text-xl text-gray-700">CRM</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">{user?.name}</span>
              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Super Admin</span>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex space-x-8">
            {[
              { id: 'leads', label: 'Leads', icon: '👥' },
              { id: 'pipeline', label: 'AI Pipeline', icon: '🤖' },
              { id: 'funnel', label: 'Funnel', icon: '📈' },
              { id: 'sequences', label: 'Sequences', icon: '🔄' },
              { id: 'stats', label: 'Dashboard', icon: '📊' },
              { id: 'templates', label: 'Email Templates', icon: '📧' },
              { id: 'accounting', label: 'Accounting', icon: '💰' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* LEADS TAB */}
        {activeTab === 'leads' && (
          <div className="grid grid-cols-12 gap-6">
            {/* Left Panel - Lead List */}
            <div className="col-span-4 space-y-4">
              {/* Automation Status Bar */}
              <div className="flex flex-wrap gap-2 items-center">
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg text-xs font-medium text-green-700">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                  Auto-capturing leads
                </div>
                <button
                  onClick={handleExport}
                  className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 text-xs font-medium"
                  data-testid="export-leads-btn"
                >
                  Export CSV
                </button>
              </div>

              {/* Search & Filter */}
              <div className="space-y-2">
                <input
                  type="text"
                  placeholder="Search leads..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && fetchLeads()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Statuses</option>
                  <option value="new">New</option>
                  <option value="contacted">Contacted</option>
                  <option value="qualified">Qualified</option>
                  <option value="proposal">Proposal</option>
                  <option value="negotiation">Negotiation</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                </select>
                <button
                  onClick={fetchLeads}
                  className="w-full px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 text-sm font-medium"
                >
                  🔍 Search
                </button>
              </div>

              {/* Lead List */}
              <div className="space-y-3 max-h-[calc(100vh-380px)] overflow-y-auto">
                {leadsLoading ? (
                  <div className="text-center py-8 text-gray-500">Loading leads...</div>
                ) : leads.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No leads found</p>
                    <p className="text-sm mt-2">Import or add leads to get started</p>
                  </div>
                ) : (
                  leads.map(lead => (
                    <LeadCard
                      key={lead.lead_id}
                      lead={lead}
                      onSelect={handleLeadSelect}
                      isSelected={selectedLead?.lead_id === lead.lead_id}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Right Panel - Lead Details */}
            <div className="col-span-8">
              {selectedLead && leadDetails ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  {/* Lead Header */}
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">{leadDetails.lead.company_name}</h2>
                      <p className="text-gray-600">{leadDetails.lead.industry}</p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <select
                        value={leadDetails.lead.status}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="qualified">Qualified</option>
                        <option value="proposal">Proposal</option>
                        <option value="negotiation">Negotiation</option>
                        <option value="won">Won</option>
                        <option value="lost">Lost</option>
                      </select>
                    </div>
                  </div>

                  {/* Contact Info Grid */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Company Info</h3>
                      <p className="text-sm"><strong>Size:</strong> {leadDetails.lead.company_size || 'N/A'}</p>
                      <p className="text-sm"><strong>Location:</strong> {leadDetails.lead.city}, {leadDetails.lead.state}</p>
                      <p className="text-sm"><strong>Website:</strong> {leadDetails.lead.website || 'N/A'}</p>
                      <p className="text-sm"><strong>Email:</strong> {leadDetails.lead.email || 'N/A'}</p>
                      <p className="text-sm"><strong>Phone:</strong> {leadDetails.lead.phone || 'N/A'}</p>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h3 className="text-sm font-medium text-gray-500 mb-2">Contact Person</h3>
                      <p className="text-sm"><strong>Name:</strong> {leadDetails.lead.contact_name || 'N/A'}</p>
                      <p className="text-sm"><strong>Title:</strong> {leadDetails.lead.contact_title || 'N/A'}</p>
                      <p className="text-sm"><strong>Email:</strong> {leadDetails.lead.contact_email || 'N/A'}</p>
                      <p className="text-sm"><strong>Phone:</strong> {leadDetails.lead.contact_phone || 'N/A'}</p>
                    </div>
                  </div>

                  {/* Value & Notes */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h3 className="text-sm font-medium text-green-700 mb-1">Estimated Value</h3>
                      <p className="text-2xl font-bold text-green-600">
                        ${(leadDetails.lead.estimated_value || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h3 className="text-sm font-medium text-blue-700 mb-1">Lead Score</h3>
                      <p className="text-2xl font-bold text-blue-600">{leadDetails.lead.score || 0}/100</p>
                    </div>
                  </div>

                  {/* Notes */}
                  {leadDetails.lead.notes && (
                    <div className="p-4 bg-yellow-50 rounded-lg mb-6">
                      <h3 className="text-sm font-medium text-yellow-700 mb-1">Notes</h3>
                      <p className="text-sm text-gray-700">{leadDetails.lead.notes}</p>
                    </div>
                  )}

                  {/* Automation Status */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {leadDetails.lead.contact_email ? (
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg text-xs font-medium text-green-700">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                        Email: {leadDetails.lead.contact_email}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-xs font-medium text-amber-700">
                        <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse"></span>
                        Waiting for email capture
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-lg text-xs font-medium text-indigo-700">
                      Pipeline: {leadDetails.lead.pipeline_status || 'new'}
                    </div>
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs font-medium text-gray-600">
                      Touches: {leadDetails.lead.pipeline_touches || 0}/3
                    </div>
                  </div>

                  {/* AI Suggestions */}
                  <div className="mb-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-indigo-900 flex items-center gap-2">
                        <span className="w-5 h-5 bg-indigo-500 rounded text-white flex items-center justify-center text-xs">AI</span>
                        Smart Suggestions
                      </h3>
                      {suggestions?.priority && (
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          suggestions.priority === 'high' ? 'bg-red-100 text-red-700' :
                          suggestions.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {suggestions.priority} priority
                        </span>
                      )}
                    </div>
                    {suggestionsLoading ? (
                      <div className="flex items-center gap-2 text-sm text-indigo-600">
                        <div className="w-4 h-4 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin"></div>
                        Analyzing lead journey...
                      </div>
                    ) : suggestions?.suggestions?.length > 0 ? (
                      <div className="space-y-2">
                        {suggestions.suggestions.map((s, i) => (
                          <div key={i} className="flex items-start gap-3 p-2.5 bg-white rounded-lg border border-indigo-100">
                            <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${
                              s.urgency === 'immediate' ? 'bg-red-500' :
                              s.urgency === 'this_week' ? 'bg-yellow-500' : 'bg-gray-400'
                            }`}></span>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{s.action}</p>
                              {s.reason && <p className="text-xs text-gray-500 mt-0.5">{s.reason}</p>}
                              {s.template && (
                                <button
                                  onClick={() => {
                                    setEmailForm({ subject: s.template.subject || '', html_content: s.template.body || s.template.html_content || '' });
                                    setShowEmailModal(true);
                                  }}
                                  className="mt-1.5 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                                >
                                  Use suggested email →
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                        {suggestions.new_score !== undefined && (
                          <p className="text-xs text-indigo-600 mt-1">Lead score updated to {suggestions.new_score}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-indigo-600">No specific suggestions at this time.</p>
                    )}
                  </div>

                  {/* Unified Customer Timeline */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Journey</h3>
                    <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                      {timelineLoading ? (
                        <div className="text-center py-6 text-gray-500">Loading timeline...</div>
                      ) : timeline.length === 0 ? (
                        <p className="text-gray-500 text-center py-4">No activity yet</p>
                      ) : (
                        timeline.map((event, idx) => {
                          const typeConfig = {
                            chatbot: { icon: '💬', bg: 'bg-purple-100', label: 'Chatbot' },
                            donation: { icon: '💰', bg: 'bg-green-100', label: 'Donation' },
                            campaign: { icon: '🎯', bg: 'bg-blue-100', label: 'Campaign' },
                            crm_email: { icon: '📧', bg: 'bg-cyan-100', label: 'Email' },
                            crm_call: { icon: '📞', bg: 'bg-orange-100', label: 'Call' },
                            crm_note: { icon: '📝', bg: 'bg-yellow-100', label: 'Note' },
                            crm_status_change: { icon: '🔄', bg: 'bg-indigo-100', label: 'Status' },
                            journey_page_view: { icon: '👁', bg: 'bg-gray-100', label: 'Page View' },
                            journey_signup: { icon: '🎉', bg: 'bg-emerald-100', label: 'Signup' },
                          };
                          const cfg = typeConfig[event.type] || { icon: '📌', bg: 'bg-gray-100', label: event.type };
                          return (
                            <div key={idx} className="flex gap-3 items-start">
                              <div className="flex flex-col items-center">
                                <div className={`w-8 h-8 rounded-full ${cfg.bg} flex items-center justify-center text-sm`}>
                                  {cfg.icon}
                                </div>
                                {idx < timeline.length - 1 && <div className="w-px h-full bg-gray-200 mt-1"></div>}
                              </div>
                              <div className="flex-1 pb-3">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-medium text-gray-500 uppercase">{cfg.label}</span>
                                  <span className="text-xs text-gray-400">
                                    {event.created_at ? new Date(event.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                  </span>
                                </div>
                                <p className="text-sm font-medium text-gray-900 mt-0.5">{event.title}</p>
                                {event.description && (
                                  <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{event.description}</p>
                                )}
                                {event.type === 'chatbot' && event.metadata?.messages_preview && (
                                  <div className="mt-1.5 p-2 bg-gray-50 rounded text-xs space-y-1">
                                    {event.metadata.messages_preview.map((m, j) => (
                                      <p key={j} className="text-gray-600 truncate">{m}</p>
                                    ))}
                                  </div>
                                )}
                                {event.metadata?.new_status && (
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    {event.metadata.old_status} → <strong>{event.metadata.new_status}</strong>
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
                  <div className="text-6xl mb-4">👈</div>
                  <h3 className="text-xl font-semibold text-gray-700">Select a Lead</h3>
                  <p className="text-gray-500 mt-2">Choose a lead from the list to view details</p>
                </div>
              )}
            </div>
          </div>
        )}



        {/* PIPELINE TAB */}
        {activeTab === 'pipeline' && (
          <div className="space-y-6">
            {/* Pipeline Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold">Autonomous Sales Pipeline</h2>
                  <p className="text-indigo-100 text-sm mt-1">Zero-human AI-powered lead conversion</p>
                </div>
                <button
                  data-testid="run-pipeline-btn"
                  onClick={runPipeline}
                  disabled={pipelineRunning}
                  className="px-5 py-2.5 bg-white text-indigo-700 rounded-lg font-semibold text-sm hover:bg-indigo-50 disabled:opacity-50 flex items-center gap-2"
                >
                  {pipelineRunning ? (
                    <><div className="w-4 h-4 border-2 border-indigo-300 border-t-indigo-700 rounded-full animate-spin"></div> Processing...</>
                  ) : (
                    <><span>▶</span> Run Pipeline</>
                  )}
                </button>
              </div>

              {/* Pipeline Status Cards */}
              {pipelineStatus && (
                <div className="grid grid-cols-5 gap-3 mt-5">
                  {[
                    { label: 'New Leads', count: pipelineStatus.pipeline_counts?.new || 0, color: 'bg-white/20' },
                    { label: 'In Outreach', count: pipelineStatus.pipeline_counts?.outreach || 0, color: 'bg-yellow-400/20' },
                    { label: 'Nurturing', count: pipelineStatus.pipeline_counts?.nurturing || 0, color: 'bg-blue-400/20' },
                    { label: 'Converted', count: pipelineStatus.pipeline_counts?.converted || 0, color: 'bg-green-400/20' },
                    { label: 'Parked', count: pipelineStatus.pipeline_counts?.parked || 0, color: 'bg-gray-400/20' },
                  ].map(s => (
                    <div key={s.label} className={`${s.color} rounded-lg p-3 text-center`}>
                      <p className="text-2xl font-bold">{s.count}</p>
                      <p className="text-xs text-indigo-100">{s.label}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Pipeline Settings */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h3 className="font-semibold text-gray-900 mb-3">Pipeline Configuration</h3>
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Score Threshold</label>
                  <p className="text-lg font-bold text-indigo-600">{pipelineStatus?.score_threshold || 80}</p>
                  <p className="text-xs text-gray-400">Minimum score for outreach</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Max Auto Touches</label>
                  <p className="text-lg font-bold text-indigo-600">{pipelineStatus?.max_touches || 3}</p>
                  <p className="text-xs text-gray-400">Emails before auto-parking</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Follow-up Interval</label>
                  <p className="text-lg font-bold text-indigo-600">{pipelineStatus?.followup_hours || 48}h</p>
                  <p className="text-xs text-gray-400">Wait time between touches</p>
                </div>
              </div>
            </div>

            {/* Auto-Scheduler */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900">Auto-Scheduler</h3>
                  <p className="text-xs text-gray-500 mt-0.5">Automatically run the pipeline on a recurring schedule</p>
                </div>
                <button
                  data-testid="scheduler-toggle-btn"
                  onClick={() => toggleScheduler(!schedulerStatus?.enabled)}
                  disabled={schedulerToggling}
                  className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                    schedulerStatus?.enabled ? 'bg-indigo-600' : 'bg-gray-300'
                  } ${schedulerToggling ? 'opacity-50' : ''}`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    schedulerStatus?.enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>

              {schedulerStatus?.enabled && (
                <div className="space-y-3">
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Run Interval</label>
                      <select
                        data-testid="scheduler-interval-select"
                        value={schedulerStatus?.interval_hours || 4}
                        onChange={(e) => toggleScheduler(true, parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="1">Every 1 hour</option>
                        <option value="2">Every 2 hours</option>
                        <option value="4">Every 4 hours</option>
                        <option value="6">Every 6 hours</option>
                        <option value="8">Every 8 hours</option>
                        <option value="12">Every 12 hours</option>
                        <option value="24">Every 24 hours</option>
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${schedulerStatus?.running ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></span>
                        <span className="text-sm font-medium text-gray-700">{schedulerStatus?.running ? 'Running' : 'Stopped'}</span>
                      </div>
                    </div>
                  </div>
                  {schedulerStatus?.next_run && (
                    <p className="text-xs text-gray-400">
                      Next run: {new Date(schedulerStatus.next_run).toLocaleString()}
                    </p>
                  )}
                  {schedulerStatus?.last_run && (
                    <p className="text-xs text-gray-400">
                      Last run: {new Date(schedulerStatus.last_run.created_at).toLocaleString()} — {schedulerStatus.last_run.leads_processed} leads processed
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* How It Works */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h3 className="font-semibold text-gray-900 mb-4">How the Pipeline Works</h3>
              <div className="flex items-center gap-2 overflow-x-auto pb-2">
                {[
                  { step: '1', title: 'Chatbot Detects Intent', desc: 'High-intent keywords trigger lead creation', color: 'bg-purple-100 text-purple-700' },
                  { step: '→', title: '', desc: '', color: 'text-gray-300' },
                  { step: '2', title: 'Score Builds', desc: 'Each interaction adds +10 to lead score', color: 'bg-blue-100 text-blue-700' },
                  { step: '→', title: '', desc: '', color: 'text-gray-300' },
                  { step: '3', title: 'AI Sends Email', desc: 'Personalized intro when score hits 80+', color: 'bg-indigo-100 text-indigo-700' },
                  { step: '→', title: '', desc: '', color: 'text-gray-300' },
                  { step: '4', title: 'Auto Follow-up', desc: 'AI follows up every 48h, up to 3 times', color: 'bg-orange-100 text-orange-700' },
                  { step: '→', title: '', desc: '', color: 'text-gray-300' },
                  { step: '5', title: 'Convert or Park', desc: 'They sign up or get auto-deprioritized', color: 'bg-green-100 text-green-700' },
                ].map((s, i) => (
                  s.title ? (
                    <div key={i} className={`flex-shrink-0 w-40 p-3 rounded-lg ${s.color}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-5 h-5 rounded-full bg-white/60 flex items-center justify-center text-xs font-bold">{s.step}</span>
                        <p className="text-xs font-semibold">{s.title}</p>
                      </div>
                      <p className="text-xs opacity-70">{s.desc}</p>
                    </div>
                  ) : (
                    <span key={i} className={`flex-shrink-0 text-xl ${s.color}`}>→</span>
                  )
                ))}
              </div>
            </div>

            {/* Pipeline Run Results */}
            {pipelineResults && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <h3 className="font-semibold text-gray-900 mb-3">
                  Last Run Results — {pipelineResults.processed} leads processed
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {pipelineResults.actions?.map((a, i) => (
                    <div key={i} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg text-sm">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        a.action?.includes('sent') ? 'bg-green-500' :
                        a.action === 'converted' ? 'bg-emerald-500' :
                        a.action === 'parked' ? 'bg-gray-400' :
                        a.action === 'waiting' ? 'bg-yellow-500' :
                        a.action === 'nurturing' ? 'bg-blue-500' :
                        'bg-gray-300'
                      }`}></span>
                      <span className="font-medium text-gray-900 w-48 truncate">{a.company || a.lead_id}</span>
                      <span className="text-gray-500">{a.action?.replace(/_/g, ' ')}</span>
                      {a.reason && <span className="text-gray-400 text-xs">({a.reason})</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Pipeline Actions */}
            {pipelineStatus?.recent_actions?.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <h3 className="font-semibold text-gray-900 mb-3">Recent AI Actions</h3>
                <div className="space-y-2">
                  {pipelineStatus.recent_actions.map((a, i) => (
                    <div key={i} className="flex items-center gap-3 p-2 text-sm border-b border-gray-50 last:border-0">
                      <span className="text-lg">🤖</span>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{a.subject}</p>
                        {a.email_sent_to && <p className="text-xs text-gray-500">→ {a.email_sent_to}</p>}
                      </div>
                      <span className="text-xs text-gray-400">
                        {a.created_at ? new Date(a.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}


        {/* FUNNEL TAB */}
        {activeTab === 'funnel' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Conversion Funnel</h2>
                  <p className="text-sm text-gray-500 mt-1">Last 30 days — Visitor to active customer journey</p>
                </div>
                <button
                  onClick={fetchFunnel}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Refresh
                </button>
              </div>
              {funnelData?.funnel ? (
                <div className="space-y-1">
                  {funnelData.funnel.map((stage, idx) => {
                    const maxCount = funnelData.funnel[0].count || 1;
                    const widthPercent = Math.max(8, (stage.count / maxCount) * 100);
                    return (
                      <div key={stage.stage} data-testid={`funnel-stage-${idx}`} className="flex items-center gap-4">
                        <div className="w-36 text-right">
                          <p className="text-sm font-medium text-gray-900">{stage.stage}</p>
                        </div>
                        <div className="flex-1 relative">
                          <div
                            className="h-12 rounded-lg flex items-center px-4 transition-all duration-500"
                            style={{
                              width: `${widthPercent}%`,
                              backgroundColor: stage.color,
                              minWidth: '60px'
                            }}
                          >
                            <span className="text-white font-bold text-sm">{stage.count.toLocaleString()}</span>
                          </div>
                        </div>
                        <div className="w-16 text-right">
                          {idx > 0 ? (
                            <span className={`text-sm font-medium ${stage.conversion_rate >= 50 ? 'text-green-600' : stage.conversion_rate >= 20 ? 'text-yellow-600' : 'text-red-600'}`}>
                              {stage.conversion_rate}%
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">Loading funnel data...</div>
              )}
            </div>

            {/* Funnel Insights */}
            {funnelData?.funnel && (
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                  <p className="text-sm text-gray-500">Total Businesses</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{funnelData.total_businesses}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                  <p className="text-sm text-gray-500">Chat → Signup Rate</p>
                  <p className="text-2xl font-bold text-indigo-600 mt-1">
                    {funnelData.funnel[2]?.conversion_rate || 0}%
                  </p>
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                  <p className="text-sm text-gray-500">Signup → Active Rate</p>
                  <p className="text-2xl font-bold text-emerald-600 mt-1">
                    {funnelData.funnel[2]?.count > 0 
                      ? Math.round((funnelData.funnel[5]?.count || 0) / funnelData.funnel[2]?.count * 100) 
                      : 0}%
                  </p>
                </div>
              </div>
            )}
          </div>
        )}


        {/* STATS TAB */}
        {activeTab === 'stats' && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-4 gap-4">
              <StatsCard
                title="Total Leads"
                value={stats?.leads?.total || 0}
                icon="👥"
                color="text-blue-600"
              />
              <StatsCard
                title="Won Deals"
                value={stats?.leads?.by_status?.won?.count || 0}
                subValue={`$${(stats?.leads?.by_status?.won?.value || 0).toLocaleString()}`}
                icon="🏆"
                color="text-green-600"
              />
              <StatsCard
                title="Total Revenue"
                value={`$${(stats?.revenue?.total_received || 0).toLocaleString()}`}
                icon="💰"
                color="text-green-600"
              />
              <StatsCard
                title="Pending Invoices"
                value={`$${(stats?.revenue?.pending || 0).toLocaleString()}`}
                icon="📄"
                color="text-orange-600"
              />
            </div>

            {/* Pipeline Breakdown */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Sales Pipeline</h3>
              <div className="grid grid-cols-7 gap-2">
                {['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'won', 'lost'].map(status => (
                  <div key={status} className="text-center p-4 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase mb-1">{status}</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {stats?.leads?.by_status?.[status]?.count || 0}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Platform Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Platform Activity</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Users</span>
                    <span className="font-semibold">{stats?.platform?.total_users || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Businesses</span>
                    <span className="font-semibold">{stats?.platform?.total_businesses || 0}</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Lead Sources</h3>
                <div className="space-y-2">
                  {stats?.leads?.by_source && Object.entries(stats.leads.by_source).map(([source, count]) => (
                    <div key={source} className="flex justify-between">
                      <span className="text-gray-600 capitalize">{source}</span>
                      <span className="font-semibold">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent Interactions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
              <div className="space-y-3">
                {stats?.recent_interactions?.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No recent activity</p>
                ) : (
                  stats?.recent_interactions?.slice(0, 5).map(interaction => (
                    <div key={interaction.interaction_id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-sm">
                        {interaction.type === 'email' ? '📧' : interaction.type === 'call' ? '📞' : '📝'}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{interaction.subject || interaction.type}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(interaction.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* TEMPLATES TAB */}
        {activeTab === 'templates' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Email Templates</h2>
              <button
                onClick={() => setShowTemplateModal(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                + New Template
              </button>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {templates.length === 0 ? (
                <div className="col-span-3 text-center py-12 bg-white rounded-xl">
                  <p className="text-gray-500">No templates yet. Create one to get started.</p>
                </div>
              ) : (
                templates.map(template => (
                  <div key={template.template_id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-gray-900">{template.name}</h3>
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">{template.category}</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{template.subject}</p>
                    <div className="text-xs text-gray-400 line-clamp-3" 
                         dangerouslySetInnerHTML={{ __html: template.html_content.substring(0, 150) + '...' }} />
                  </div>
                ))
              )}
            </div>
          </div>
        )}


        {/* SEQUENCES TAB */}
        {activeTab === 'sequences' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Automated Email Sequences</h2>
                <p className="text-sm text-gray-500 mt-1">All emails are AI-generated and sent automatically — no setup needed</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                <span className="text-sm font-medium text-green-700">Pipeline Active</span>
              </div>
            </div>

            {/* Auto Sequence Flow Diagram */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">How the Pipeline Works</h3>
              <div className="flex items-center gap-3 overflow-x-auto pb-2">
                {[
                  { label: 'Visitor chats', sublabel: 'Lead auto-created', color: 'bg-blue-500' },
                  { label: 'Email captured', sublabel: 'Ari asks for email', color: 'bg-indigo-500' },
                  { label: 'Intro email sent', sublabel: 'AI-personalized', color: 'bg-purple-500' },
                  { label: 'Score builds', sublabel: '+5/+15/+20 per action', color: 'bg-amber-500' },
                  { label: 'Follow-up #2', sublabel: 'After 48h, auto-sent', color: 'bg-orange-500' },
                  { label: 'Follow-up #3', sublabel: 'Final nudge', color: 'bg-red-500' },
                  { label: 'Auto-parked', sublabel: 'If no response', color: 'bg-gray-500' },
                ].map((step, i) => (
                  <div key={i} className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-center">
                      <div className={`w-10 h-10 ${step.color} rounded-full flex items-center justify-center text-white font-bold text-sm mx-auto mb-1`}>
                        {i + 1}
                      </div>
                      <p className="text-xs font-medium text-gray-900 whitespace-nowrap">{step.label}</p>
                      <p className="text-[10px] text-gray-500 whitespace-nowrap">{step.sublabel}</p>
                    </div>
                    {i < 6 && <div className="w-8 h-0.5 bg-gray-300 flex-shrink-0 mt-[-16px]"></div>}
                  </div>
                ))}
              </div>
            </div>

            {/* Live Pipeline Metrics */}
            <div className="grid grid-cols-4 gap-4">
              {[
                { label: 'Leads Created', value: leads.length, sublabel: 'From chatbot', color: 'text-blue-600', bg: 'bg-blue-50' },
                { label: 'Emails Captured', value: leads.filter(l => l.contact_email || l.email).length, sublabel: 'Ready for outreach', color: 'text-green-600', bg: 'bg-green-50' },
                { label: 'Emails Sent', value: leads.reduce((sum, l) => sum + (l.pipeline_touches || 0), 0), sublabel: 'Auto-generated', color: 'text-purple-600', bg: 'bg-purple-50' },
                { label: 'Avg Score', value: leads.length ? Math.round(leads.reduce((sum, l) => sum + (l.score || 0), 0) / leads.length) : 0, sublabel: 'Engagement level', color: 'text-amber-600', bg: 'bg-amber-50' },
              ].map((metric, i) => (
                <div key={i} className={`${metric.bg} rounded-xl p-4 border`}>
                  <p className={`text-2xl font-bold ${metric.color}`}>{metric.value}</p>
                  <p className="text-sm font-medium text-gray-900">{metric.label}</p>
                  <p className="text-xs text-gray-500">{metric.sublabel}</p>
                </div>
              ))}
            </div>

            {/* Recent Auto-Emails Log */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h3 className="font-semibold text-gray-900 mb-3">Recent Pipeline Activity</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {leads.filter(l => l.pipeline_touches > 0).length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No automated emails sent yet. Leads need to share their email in the chatbot first.</p>
                ) : (
                  leads.filter(l => l.pipeline_touches > 0).sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || '')).map(lead => (
                    <div key={lead.lead_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{lead.company_name}</p>
                        <p className="text-xs text-gray-500">{lead.contact_email || lead.email} — {lead.pipeline_touches} email(s) sent</p>
                      </div>
                      <div className="text-right">
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                          lead.pipeline_status === 'converted' ? 'bg-green-100 text-green-700' :
                          lead.pipeline_status === 'outreach' ? 'bg-blue-100 text-blue-700' :
                          lead.pipeline_status === 'parked' ? 'bg-gray-100 text-gray-600' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>{lead.pipeline_status}</span>
                        <p className="text-[10px] text-gray-400 mt-0.5">Score: {lead.score}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}


        {/* ACCOUNTING TAB */}
        {activeTab === 'accounting' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">Accounting</h2>
              <button
                onClick={() => setShowAddEntry(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                + New Entry
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {accountingEntries.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        No accounting entries yet
                      </td>
                    </tr>
                  ) : (
                    accountingEntries.map(entry => (
                      <tr key={entry.entry_id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm capitalize">{entry.type}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm capitalize">{entry.category}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          ${entry.amount?.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <StatusBadge status={entry.status} />
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-600 truncate max-w-xs">
                          {entry.description || '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(entry.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* MODALS */}
      

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl">
            <h2 className="text-xl font-bold mb-4">Create Email Template</h2>
            <form onSubmit={handleCreateTemplate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                  <input
                    type="text"
                    required
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate({...newTemplate, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={newTemplate.category}
                    onChange={(e) => setNewTemplate({...newTemplate, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="outreach">Outreach</option>
                    <option value="follow_up">Follow Up</option>
                    <option value="proposal">Proposal</option>
                    <option value="onboarding">Onboarding</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  required
                  value={newTemplate.subject}
                  onChange={(e) => setNewTemplate({...newTemplate, subject: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Use {{company_name}}, {{contact_name}} for variables"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">HTML Content</label>
                <textarea
                  required
                  value={newTemplate.html_content}
                  onChange={(e) => setNewTemplate({...newTemplate, html_content: e.target.value})}
                  rows={10}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                  placeholder="<p>Hello {{contact_name}},</p>&#10;<p>I'm reaching out from Altrunic...</p>"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Variables: {"{{company_name}}, {{contact_name}}, {{contact_title}}, {{industry}}, {{city}}, {{state}}"}
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowTemplateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Template
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Accounting Entry Modal */}
      {showAddEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Add Accounting Entry</h2>
            <form onSubmit={handleCreateEntry} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={newEntry.type}
                    onChange={(e) => setNewEntry({...newEntry, type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="invoice">Invoice</option>
                    <option value="payment">Payment</option>
                    <option value="expense">Expense</option>
                    <option value="refund">Refund</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={newEntry.category}
                    onChange={(e) => setNewEntry({...newEntry, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="subscription">Subscription</option>
                    <option value="setup_fee">Setup Fee</option>
                    <option value="consulting">Consulting</option>
                    <option value="donation_processing">Donation Processing</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={newEntry.amount}
                  onChange={(e) => setNewEntry({...newEntry, amount: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={newEntry.due_date}
                  onChange={(e) => setNewEntry({...newEntry, due_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newEntry.description}
                  onChange={(e) => setNewEntry({...newEntry, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddEntry(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Entry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRMDashboard;
